/**
 * Market Hours & Exchange Session Schedule Utility
 * 
 * Rules:
 * 1. Crypto: 24/7/365 (Always open, immune to all session/weekend closures)
 * 2. Forex: Opens Sunday 17:00 NY -> Closes Friday 17:00 NY (Closed weekends)
 * 3. US Stocks (NYSE/NASDAQ): Mon-Fri 09:30 - 16:00 America/New_York (Closed weekends & outside 9:30-16:00)
 * 4. BIST (Borsa İstanbul): Mon-Fri 10:00 - 18:00 Europe/Istanbul (Closed weekends & outside 10:00-18:00 TSİ)
 * 5. European Stocks (XETR/EURONEXT): Mon-Fri 09:00 - 17:30 Europe/Berlin (Closed weekends & outside hours)
 * 6. Commodities & Indices: Opens Sunday 18:00 NY -> Closes Friday 17:00 NY (Daily break 17:00-18:00 NY, closed weekends)
 */

import { useState, useEffect } from 'react';

const BIST_SYMBOLS = new Set([
  'THYAO', 'GARAN', 'AKBNK', 'EREGL', 'KCHOL', 'SAHOL', 'SISE', 'TCELL', 'TUPRS',
  'BIMAS', 'A1CAP', 'VERUS', 'ISCTR', 'VAKBN', 'ASELS', 'FROTO', 'TOASO', 'ARCLK',
  'PGSUS', 'HALKB', 'YKBNK', 'TTKOM', 'PETKM', 'KOZAL', 'KRDMD', 'SASA', 'HEKTS'
]);

export const getMarketCategory = (symbol = '', category = '') => {
  const normCat = (category || '').toLowerCase();
  if (normCat === 'crypto' || normCat === 'cryptocurrency') return 'crypto';
  if (normCat === 'forex') return 'forex';
  if (normCat === 'commodities' || normCat === 'commodity') return 'commodities';
  if (normCat === 'indices' || normCat === 'index') return 'indices';
  if (normCat === 'bist' || normCat === 'stocks_bist') return 'bist';
  if (normCat === 'eu_stocks' || normCat === 'stocks_eu') return 'stocks_eu';
  if (normCat === 'stocks_us' || normCat === 'stocks') return 'stocks_us';

  const s = symbol.toUpperCase().replace('/', '').trim();
  const cleanS = s.replace('.IS', '').replace('-IST', '');

  if (s.endsWith('.IS') || s.endsWith('-IST') || s.startsWith('BIST:') || BIST_SYMBOLS.has(cleanS)) {
    return 'bist';
  }
  if (['BTC', 'ETH', 'SOL', 'XRP', 'BNB', 'DOGE', 'ADA', 'AVAX', 'DOT', 'LTC', 'LINK', 'UNI', 'SHIB', 'NEAR', 'MATIC'].some(c => s.startsWith(c))) {
    return 'crypto';
  }
  if (['EUR', 'GBP', 'USD', 'JPY', 'CHF', 'AUD', 'CAD', 'NZD', 'TRY', 'ZAR', 'MXN', 'SGD'].includes(s.slice(0, 3)) && s.length === 6) {
    return 'forex';
  }
  if (symbol.includes('/')) return 'forex';
  if (['XAU', 'XAG', 'XPT', 'XPD', 'USOIL', 'UKOIL', 'WTI', 'BRENT', 'NATGAS', 'COPPER', 'CL', 'BZ', 'HG', 'NG'].some(c => s.startsWith(c) || s === c)) {
    return 'commodities';
  }
  if (['US100', 'US500', 'US30', 'NAS100', 'GER40', 'UK100', 'DAX', 'XU100', 'XU030', 'JP225', 'HK50', 'FRA40', 'EU50'].some(c => s.startsWith(c))) {
    return 'indices';
  }
  if (['.DE', '.PA', '.SW', '.AS', '.L', '.MC', '.MI'].some(ext => s.endsWith(ext))) {
    return 'stocks_eu';
  }

  return 'stocks_us';
};

/**
 * Returns localized day and decimal hour for a given IANA timeZone
 */
export const getZonedTime = (timeZone) => {
  const now = new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour12: false,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).formatToParts(now);

    const map = {};
    for (const p of parts) map[p.type] = p.value;

    const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(map.weekday);
    const year = parseInt(map.year, 10);
    const month = parseInt(map.month, 10);
    const day = parseInt(map.day, 10);
    const hour = parseInt(map.hour, 10);
    const minute = parseInt(map.minute, 10);
    const second = parseInt(map.second, 10);
    const decimalHour = hour + minute / 60 + second / 3600;
    return { dayOfWeek, year, month, day, hour, minute, second, decimalHour };
  } catch (e) {
    const utcDay = now.getUTCDay();
    const utcHour = now.getUTCHours() + now.getUTCMinutes() / 60;
    return {
      dayOfWeek: utcDay,
      year: now.getUTCFullYear(),
      month: now.getUTCMonth() + 1,
      day: now.getUTCDate(),
      hour: now.getUTCHours(),
      minute: now.getUTCMinutes(),
      second: now.getUTCSeconds(),
      decimalHour: utcHour
    };
  }
};

/**
 * Converts a target local date/time in an IANA timeZone to a real UTC Date object
 */
export const getZonedTargetTime = (targetYear, targetMonth, targetDay, targetHour, targetMinute, timeZone) => {
  try {
    const approx = new Date(Date.UTC(targetYear, targetMonth - 1, targetDay, targetHour, targetMinute));
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
    }).formatToParts(approx);
    const map = {};
    for (const p of parts) map[p.type] = p.value;
    const zonedHour = parseInt(map.hour, 10);
    const zonedMinute = parseInt(map.minute, 10);
    const zonedDay = parseInt(map.day, 10);
    const diffMinutes = (zonedDay - targetDay) * 1440 + (zonedHour - targetHour) * 60 + (zonedMinute - targetMinute);
    return new Date(approx.getTime() - diffMinutes * 60 * 1000);
  } catch (err) {
    return new Date(Date.UTC(targetYear, targetMonth - 1, targetDay, targetHour, targetMinute));
  }
};

const getTargetDayDate = (daysAhead, targetHour, targetMinute, timeZone) => {
  const now = new Date();
  const targetDayDate = new Date(now.getTime() + daysAhead * 86400000);
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(targetDayDate);
    const map = {};
    for (const p of parts) map[p.type] = p.value;
    return getZonedTargetTime(
      parseInt(map.year, 10),
      parseInt(map.month, 10),
      parseInt(map.day, 10),
      targetHour,
      targetMinute,
      timeZone
    );
  } catch (e) {
    targetDayDate.setUTCHours(targetHour, targetMinute, 0, 0);
    return targetDayDate;
  }
};

/**
 * Calculates countdown units from now to targetDate
 */
const formatCountdown = (targetDate) => {
  if (!targetDate) {
    return {
      totalSeconds: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      digitalCountdown: '00:00:00',
      countdownTextTR: '',
      countdownTextEN: '',
    };
  }

  const diffMs = Math.max(0, targetDate.getTime() - Date.now());
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n) => String(n).padStart(2, '0');
  const digitalCountdown = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  const countdownTextTR = `${hours} saat, ${minutes} dakika, ${seconds} saniye`;
  const countdownTextEN = `${hours} hours, ${minutes} minutes, ${seconds} seconds`;

  return {
    totalSeconds,
    hours,
    minutes,
    seconds,
    digitalCountdown,
    countdownTextTR,
    countdownTextEN,
  };
};

/**
 * Evaluates whether the market for a given symbol is currently OPEN or CLOSED,
 * and calculates the exact next opening time and countdown.
 */
export const checkMarketHours = (symbol, category = '', serverNextOpen = null) => {
  const cat = getMarketCategory(symbol, category);

  // 1. CRYPTO: 100% immune, 24/7/365
  if (cat === 'crypto') {
    return {
      isOpen: true,
      isClosed: false,
      status: 'open',
      reason: 'Open 24/7',
      marketName: 'Spot Crypto',
      marketCategory: 'crypto',
      workingHours: '24/7 / 365',
      timeZone: 'UTC',
      localOpenTimeStr: '24/7 Kesintisiz',
      isCrypto: true,
      nextOpenDate: null,
      nextOpenIso: null,
      ...formatCountdown(null),
    };
  }

  // If server provided authoritative next_open and it's valid
  let serverTarget = null;
  if (serverNextOpen) {
    const parsed = new Date(serverNextOpen);
    if (!isNaN(parsed.getTime()) && parsed.getTime() > Date.now()) {
      serverTarget = parsed;
    }
  }

  // 2. FOREX: Sunday 17:00 NY -> Friday 17:00 NY
  if (cat === 'forex') {
    const ny = getZonedTime('America/New_York');
    const isSat = ny.dayOfWeek === 6;
    const isSunBeforeOpen = ny.dayOfWeek === 0 && ny.decimalHour < 17;
    const isFriAfterClose = ny.dayOfWeek === 5 && ny.decimalHour >= 17;
    const isClosed = isSat || isSunBeforeOpen || isFriAfterClose;

    if (isClosed) {
      let daysAhead = 0;
      if (isFriAfterClose) daysAhead = 2; // Fri -> Sun
      else if (isSat) daysAhead = 1;      // Sat -> Sun
      else if (isSunBeforeOpen) daysAhead = 0; // Sun today

      const nextOpen = serverTarget || getTargetDayDate(daysAhead, 17, 0, 'America/New_York');
      return {
        isOpen: false,
        isClosed: true,
        status: 'closed',
        reason: isSat || isSunBeforeOpen || isFriAfterClose
          ? 'Hafta sonu · Döviz piyasası Pazar 17:00 NY’de açılır'
          : 'Döviz piyasası kapalı',
        marketName: 'Currency Pairs',
        marketCategory: 'forex',
        workingHours: 'Pazar 17:00 – Cuma 17:00 EST/EDT',
        timeZone: 'America/New_York',
        localOpenTimeStr: 'Pazar 17:00 EST/EDT (New York)',
        isCrypto: false,
        nextOpenDate: nextOpen,
        nextOpenIso: nextOpen.toISOString(),
        ...formatCountdown(nextOpen),
      };
    }

    return {
      isOpen: true,
      isClosed: false,
      status: 'open',
      reason: 'Piyasa Açık',
      marketName: 'Currency Pairs',
      marketCategory: 'forex',
      workingHours: 'Pazar 17:00 – Cuma 17:00 EST/EDT',
      timeZone: 'America/New_York',
      localOpenTimeStr: 'Seans Aktif',
      isCrypto: false,
      nextOpenDate: null,
      nextOpenIso: null,
      ...formatCountdown(null),
    };
  }

  // 3. COMMODITIES & INDICES: Sunday 18:00 NY -> Friday 17:00 NY
  if (cat === 'commodities' || cat === 'indices') {
    const ny = getZonedTime('America/New_York');
    const label = cat === 'commodities' ? 'Emtia CFD' : 'Endeks CFD';
    const isSat = ny.dayOfWeek === 6;
    const isSunBeforeOpen = ny.dayOfWeek === 0 && ny.decimalHour < 18;
    const isFriAfterClose = ny.dayOfWeek === 5 && ny.decimalHour >= 17;
    const isDailyBreak = ny.dayOfWeek >= 1 && ny.dayOfWeek <= 4 && ny.decimalHour >= 17 && ny.decimalHour < 18;
    const isClosed = isSat || isSunBeforeOpen || isFriAfterClose || isDailyBreak;

    if (isClosed) {
      let daysAhead = 0;
      let targetH = 18;
      let localStr = 'Pazar 18:00 EST/EDT (New York)';
      if (isDailyBreak) {
        daysAhead = 0;
        targetH = 18;
        localStr = 'Bugün 18:00 EST/EDT (Günlük Seans Arası)';
      } else if (isFriAfterClose) {
        daysAhead = 2;
      } else if (isSat) {
        daysAhead = 1;
      } else if (isSunBeforeOpen) {
        daysAhead = 0;
      }

      const nextOpen = serverTarget || getTargetDayDate(daysAhead, targetH, 0, 'America/New_York');
      return {
        isOpen: false,
        isClosed: true,
        status: 'closed',
        reason: isDailyBreak
          ? `${label} seans arası (17:00–18:00 EST/EDT)`
          : `${label} hafta sonu tatili (Açılış Pazar 18:00 NY)`,
        marketName: label,
        marketCategory: cat,
        workingHours: 'Pazar 18:00 – Cuma 17:00 EST/EDT',
        timeZone: 'America/New_York',
        localOpenTimeStr: localStr,
        isCrypto: false,
        nextOpenDate: nextOpen,
        nextOpenIso: nextOpen.toISOString(),
        ...formatCountdown(nextOpen),
      };
    }

    return {
      isOpen: true,
      isClosed: false,
      status: 'open',
      reason: 'Piyasa Açık',
      marketName: label,
      marketCategory: cat,
      workingHours: 'Pazar 18:00 – Cuma 17:00 EST/EDT',
      timeZone: 'America/New_York',
      localOpenTimeStr: 'Seans Aktif',
      isCrypto: false,
      nextOpenDate: null,
      nextOpenIso: null,
      ...formatCountdown(null),
    };
  }

  // 4. BIST (Borsa İstanbul): Mon-Fri 10:00 - 18:00 Istanbul time (Europe/Istanbul UTC+3)
  if (cat === 'bist') {
    const ist = getZonedTime('Europe/Istanbul');
    const isWeekend = ist.dayOfWeek === 0 || ist.dayOfWeek === 6;
    const isOutsideHours = ist.decimalHour < 10 || ist.decimalHour >= 18;
    const isClosed = isWeekend || isOutsideHours;

    if (isClosed) {
      let daysAhead = 0;
      let localStr = 'Pazartesi 10:00 TSİ (Europe/Istanbul)';

      if (ist.dayOfWeek === 6) { // Saturday
        daysAhead = 2;
      } else if (ist.dayOfWeek === 0) { // Sunday
        daysAhead = 1;
      } else if (ist.dayOfWeek === 5 && ist.decimalHour >= 18) { // Friday evening
        daysAhead = 3;
      } else if (ist.decimalHour >= 18) { // Mon-Thu evening
        daysAhead = 1;
        localStr = 'Yarın 10:00 TSİ (Europe/Istanbul)';
      } else if (ist.decimalHour < 10) { // Mon-Fri morning
        daysAhead = 0;
        localStr = 'Bugün 10:00 TSİ (Europe/Istanbul)';
      }

      const nextOpen = serverTarget || getTargetDayDate(daysAhead, 10, 0, 'Europe/Istanbul');
      return {
        isOpen: false,
        isClosed: true,
        status: 'closed',
        reason: isWeekend
          ? 'Borsa İstanbul hafta sonu kapalı (Açılış Pazartesi 10:00 TSİ)'
          : 'Borsa İstanbul seans dışı (10:00–18:00 TSİ)',
        marketName: 'Borsa İstanbul',
        marketCategory: 'bist',
        workingHours: 'Hafta içi 10:00–18:00 TSİ',
        timeZone: 'Europe/Istanbul',
        localOpenTimeStr: localStr,
        isCrypto: false,
        nextOpenDate: nextOpen,
        nextOpenIso: nextOpen.toISOString(),
        ...formatCountdown(nextOpen),
      };
    }

    return {
      isOpen: true,
      isClosed: false,
      status: 'open',
      reason: 'Piyasa Açık',
      marketName: 'Borsa İstanbul',
      marketCategory: 'bist',
      workingHours: 'Hafta içi 10:00–18:00 TSİ',
      timeZone: 'Europe/Istanbul',
      localOpenTimeStr: 'Seans Aktif (10:00–18:00 TSİ)',
      isCrypto: false,
      nextOpenDate: null,
      nextOpenIso: null,
      ...formatCountdown(null),
    };
  }

  // 5. EUROPEAN STOCKS: Mon-Fri 09:00 - 17:30 Berlin/Frankfurt (Europe/Berlin)
  if (cat === 'stocks_eu') {
    const ber = getZonedTime('Europe/Berlin');
    const isWeekend = ber.dayOfWeek === 0 || ber.dayOfWeek === 6;
    const isOutsideHours = ber.decimalHour < 9 || ber.decimalHour >= 17.5;
    const isClosed = isWeekend || isOutsideHours;

    if (isClosed) {
      let daysAhead = 0;
      let localStr = 'Pazartesi 09:00 CET/CEST (Frankfurt)';

      if (ber.dayOfWeek === 6) daysAhead = 2;
      else if (ber.dayOfWeek === 0) daysAhead = 1;
      else if (ber.dayOfWeek === 5 && ber.decimalHour >= 17.5) daysAhead = 3;
      else if (ber.decimalHour >= 17.5) {
        daysAhead = 1;
        localStr = 'Yarın 09:00 CET/CEST (Frankfurt)';
      } else if (ber.decimalHour < 9) {
        daysAhead = 0;
        localStr = 'Bugün 09:00 CET/CEST (Frankfurt)';
      }

      const nextOpen = serverTarget || getTargetDayDate(daysAhead, 9, 0, 'Europe/Berlin');
      return {
        isOpen: false,
        isClosed: true,
        status: 'closed',
        reason: isWeekend
          ? 'Avrupa Borsaları hafta sonu kapalı (Açılış Pazartesi 09:00 CET)'
          : 'Avrupa Borsaları seans dışı (09:00–17:30 CET)',
        marketName: 'Avrupa Pay Piyasaları',
        marketCategory: 'stocks_eu',
        workingHours: 'Hafta içi 09:00–17:30 CET/CEST',
        timeZone: 'Europe/Berlin',
        localOpenTimeStr: localStr,
        isCrypto: false,
        nextOpenDate: nextOpen,
        nextOpenIso: nextOpen.toISOString(),
        ...formatCountdown(nextOpen),
      };
    }

    return {
      isOpen: true,
      isClosed: false,
      status: 'open',
      reason: 'Piyasa Açık',
      marketName: 'Avrupa Pay Piyasaları',
      marketCategory: 'stocks_eu',
      workingHours: 'Hafta içi 09:00–17:30 CET/CEST',
      timeZone: 'Europe/Berlin',
      localOpenTimeStr: 'Seans Aktif',
      isCrypto: false,
      nextOpenDate: null,
      nextOpenIso: null,
      ...formatCountdown(null),
    };
  }

  // 6. US STOCKS (NYSE/NASDAQ): Mon-Fri 09:30 - 16:00 New York (America/New_York)
  const ny = getZonedTime('America/New_York');
  const isWeekend = ny.dayOfWeek === 0 || ny.dayOfWeek === 6;
  const isOutsideHours = ny.decimalHour < 9.5 || ny.decimalHour >= 16;
  const isClosed = isWeekend || isOutsideHours;

  if (isClosed) {
    let daysAhead = 0;
    let localStr = 'Pazartesi 09:30 EST/EDT (New York)';

    if (ny.dayOfWeek === 6) daysAhead = 2;
    else if (ny.dayOfWeek === 0) daysAhead = 1;
    else if (ny.dayOfWeek === 5 && ny.decimalHour >= 16) daysAhead = 3;
    else if (ny.decimalHour >= 16) {
      daysAhead = 1;
      localStr = 'Yarın 09:30 EST/EDT (New York)';
    } else if (ny.decimalHour < 9.5) {
      daysAhead = 0;
      localStr = 'Bugün 09:30 EST/EDT (New York)';
    }

    const nextOpen = serverTarget || getTargetDayDate(daysAhead, 9, 30, 'America/New_York');
    return {
      isOpen: false,
      isClosed: true,
      status: 'closed',
      reason: isWeekend
        ? 'ABD Pay Piyasaları hafta sonu kapalı (Açılış Pazartesi 09:30 EST)'
        : 'ABD Pay Piyasaları seans dışı (09:30–16:00 EST/EDT)',
      marketName: 'NYSE / NASDAQ',
      marketCategory: 'stocks_us',
      workingHours: 'Hafta içi 09:30–16:00 EST/EDT',
      timeZone: 'America/New_York',
      localOpenTimeStr: localStr,
      isCrypto: false,
      nextOpenDate: nextOpen,
      nextOpenIso: nextOpen.toISOString(),
      ...formatCountdown(nextOpen),
    };
  }

  return {
    isOpen: true,
    isClosed: false,
    status: 'open',
    reason: 'Piyasa Açık',
    marketName: 'NYSE / NASDAQ',
    marketCategory: 'stocks_us',
    workingHours: 'Hafta içi 09:30–16:00 EST/EDT',
    timeZone: 'America/New_York',
    localOpenTimeStr: 'Seans Aktif',
    isCrypto: false,
    nextOpenDate: null,
    nextOpenIso: null,
    ...formatCountdown(null),
  };
};

/**
 * React hook to track real-time market open/closed status and live ticking countdown
 */
export const useMarketStatus = (symbol, category = '', serverNextOpen = null) => {
  const [status, setStatus] = useState(() => checkMarketHours(symbol, category, serverNextOpen));

  useEffect(() => {
    // Immediate initial check
    setStatus(checkMarketHours(symbol, category, serverNextOpen));

    // Re-check and tick countdown every 1 second (1000ms)
    const interval = setInterval(() => {
      setStatus(checkMarketHours(symbol, category, serverNextOpen));
    }, 1000);

    return () => clearInterval(interval);
  }, [symbol, category, serverNextOpen]);

  return status;
};

export const useMarketCountdown = useMarketStatus;
